import { useContext } from "react";
import { FIRMTableProps, FIRMTableTooltip } from "..";
import { AuthContext } from "../../../../../Authorization/AuthContext";
import { FIRMWithWarnings } from "common-client/utils/firmInfoWarnings";
import { getBaseFloodElevationConfiguration } from "common-client/utils/firms";
import { isNil } from "lodash";
import { RequestInfoLink } from "../__styles__/FIRMTable";
import { buildLink } from "common/routing";
import { track } from "../../../../../../utils/tracking";
import React from "react";
import { FIRMInfoRow } from "./FIRMInfoRow";
import { RenderMode } from "../../types";
import { DisplayConfig } from "../../../../../../generated/graphql";
import { LayerContext } from "../../../../../Maps/layers";
import { ApproximateBfeTool } from "../../../../../Maps/ApproximateBfeTool";
import { canUseApproximateBfeTool } from "../../../../../Maps/ApproximateBfeTool/utils";
import { useWindowSize } from "../../../../../../hooks/useWindowSize";
import { formatFeet } from "common/utils/strings";
import { FIRMInfoContext } from "../../FIRMInfoContext";

type BFERowProps = Pick<FIRMTableProps, "property" | "onFIRMEdit"> & {
  canUpdateFIRMInfo: boolean;
  hasRequestHelpOption?: boolean;
  label: string;
  isPublic: boolean;
  tooltip?: React.ReactNode;
  size: RenderMode;
  displayConfig?: DisplayConfig;
};

export const BFERow = ({
  property,
  onFIRMEdit,
  hasRequestHelpOption = false,
  isPublic,
  tooltip,
  label,
  size,
  displayConfig,
}: BFERowProps) => {
  const { isGuest, authorized } = useContext(AuthContext);
  const { activeFirmIds, firms } = useContext(FIRMInfoContext);
  const { approximateBfeToolDispatch, visibleFIRM } = useContext(LayerContext);
  const activeFirms = firms.filter(firm => activeFirmIds.includes(firm.id));
  const windowWidth = useWindowSize().width;

  const { skipIfBlank } = getBaseFloodElevationConfiguration({
    firms: activeFirms,
    authorized,
    isGuest,
    hasRequestHelpOption,
  });

  const valueForFIRM = (firm: FIRMWithWarnings) => {
    const mapLayerFirm = visibleFIRM();

    const openApproximateBfeTool =
      canUseApproximateBfeTool({
        displayConfig,
        firm,
        windowWidth,
      }) &&
      property?.id &&
      mapLayerFirm &&
      !firm.stringStaticBFE;

    const onClick = openApproximateBfeTool
      ? () => {
          approximateBfeToolDispatch?.({
            type: "setMode",
            data: {
              mode: "on",
              render: () => {
                return (
                  <ApproximateBfeTool
                    openEditFIRM={() => onFIRMEdit({ firm, mode: "elevation" })}
                    propertyId={property.id}
                    mapLayerFirm={mapLayerFirm}
                  />
                );
              },
            },
          });
        }
      : () => onFIRMEdit({ firm, mode: "elevation" });

    const { value, canEdit, canRequestHelp } =
      getBaseFloodElevationConfiguration({
        selectedFirmId: firm.id,
        firms: [firm],
        authorized,
        isGuest,
        hasRequestHelpOption,
      });

    if (isNil(value) && canRequestHelp) {
      return (
        <RequestInfoLink
          to={buildLink(
            "getHelp",
            {},
            { propertyId: property?.id, reasonCodes: "requestBFE" }
          )}
          onClick={() => track("Request BFE")}
        >
          Request info
        </RequestInfoLink>
      );
    }

    const formattedValue = isNil(value) ? null : formatFeet(value);

    if (canEdit) {
      return <a onClick={onClick}>{formattedValue ?? "Add"}</a>;
    }

    return formattedValue ?? null;
  };

  const activeBFE = activeFirms[0]?.bfeSource;
  const finalTooltip = activeBFE ? (
    <FIRMTableTooltip tooltipMarkdown={activeBFE} isGuestOnly={false} />
  ) : (
    tooltip
  );

  return (
    <FIRMInfoRow
      property="stringStaticBFE"
      valueForFIRM={valueForFIRM}
      label={label}
      skipIfBlank={skipIfBlank}
      tooltip={finalTooltip}
      isPublic={isPublic}
      size={size}
    />
  );
};
